import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSite } from '../../api/sites';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { CustomerSite } from '../../types/api';

// Per-site order-value approval threshold (§ Perry: spending limits, routed
// by amount, per-site). Left blank = old behaviour (needs approval only if
// the site has an active signing authority). Set = orders under it
// auto-approve regardless, orders at/above it always require a signing
// authority - see OrdersService.resolveNeedsApproval for the full rule and
// what happens if the site has no signing authority to enforce it against.
export function SiteApprovalModal({
  orgId,
  site,
  open,
  onClose,
}: {
  orgId: string;
  site: CustomerSite | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue(site?.approvalThreshold ?? '');
    setError(null);
  }, [open, site]);

  const mutation = useMutation({
    mutationFn: () => updateSite(orgId, site!.id, { approvalThreshold: value.trim() === '' ? null : Number(value) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites', orgId] });
      showToast('Approval threshold updated.');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update the approval threshold.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (value.trim() !== '' && (Number.isNaN(Number(value)) || Number(value) < 0)) {
      setError('Enter a positive number, or leave blank for no threshold.');
      return;
    }
    mutation.mutate();
  }

  if (!site) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Approval threshold - ${site.name}`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="site-approval-form" type="submit" isLoading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="site-approval-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-slate-500">
          Orders under this amount at this site auto-approve, even if there's a signing authority. Orders at or above
          it always need signing-authority approval - if the site has none configured, submitting an order this size
          is blocked with a clear message instead of going through unchecked. Leave blank to use the default
          behaviour (needs approval only when the site has a signing authority).
        </p>
        <Input
          label="Threshold (£)"
          type="number"
          step="0.01"
          min="0"
          placeholder="No threshold"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </form>
    </Modal>
  );
}
